/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Icon } from '@iwoca/orion';
import classNames from 'classnames';
import {
    FormattedMessage,
    injectIntl,
    WrappedComponentProps
} from 'react-intl';

import {
    availableSubTitlesType,
    generateBodyTextString,
    generateCopyStringBody,
    requirementsSubTitle,
    requirementsTitle
} from './BusinessAccountsShareRequirements.helpers';
import messages from './BusinessAccountsShareRequirements.messages';
import styles from './BusinessAccountsShareRequirements.module.css';
import { getAmplitudeDefaultEventProperties } from '../../../tracking/amplitudeEvents';
import { DocRequirementTypes, TOffersDataRequirements } from '../types';

type Props = {
    dataRequirements: TOffersDataRequirements;
    locale: string;
} & WrappedComponentProps;

const BusinessAccountsShareRequirements = ({
    dataRequirements,
    locale,
    intl
}: Props) => {
    const [isContentOpen, setIsContentOpen] = useState(false);
    const [isCopyClicked, setIsCopyClicked] = useState(false);
    const requirementListRef = useRef<HTMLDivElement>(null);

    const requirementCategory = dataRequirements.filter(
        (requirement) => requirement.category === 'documents'
    );

    const requiredDocs = requirementCategory[0].actions.filter(
        (action) => action.is_optional === false
    );

    const optionalDocs = requirementCategory[0].actions.filter(
        (action) => action.is_optional === true
    );

    function renderedDocumentRequirements() {
        const requiredDocsText = requiredDocs.map((requirement, index) => {
            return (
                <li key={index}>
                    {requirementsTitle(
                        requirement.type as DocRequirementTypes,
                        requirement.quantifier.value,
                        intl
                    )}
                    <br />
                    <span className={styles.docRequirementSubTitle}>
                        {requirementsSubTitle(
                            requirement.type as availableSubTitlesType,
                            requirement.quantifier.value,
                            intl
                        )}
                    </span>
                </li>
            );
        });

        const optionalDocsText = optionalDocs.map((requirement, index) => {
            return (
                <li key={index}>
                    {requirementsTitle(
                        requirement.type as DocRequirementTypes,
                        requirement.quantifier.value,
                        intl
                    )}{' '}
                    (optional)
                    <br />
                    <span className={styles.docRequirementSubTitle}>
                        {requirementsSubTitle(
                            requirement.type as availableSubTitlesType,
                            requirement.quantifier.value,
                            intl
                        )}
                    </span>
                </li>
            );
        });

        return (
            <>
                {requiredDocsText && <ul>{requiredDocsText}</ul>}
                {optionalDocsText && locale === 'de' && (
                    <ul>{optionalDocsText}</ul>
                )}
            </>
        );
    }

    function copyToClipBoard() {
        let textToCopy: string = '';
        if (requiredDocs && optionalDocs && locale === 'de') {
            textToCopy = generateCopyStringBody({
                requiredDocs,
                optionalDocs,
                intl: intl
            });
        } else if (requiredDocs) {
            if (requiredDocs) {
                textToCopy = generateCopyStringBody({
                    requiredDocs,
                    intl: intl
                });
            }
        }

        textToCopy =
            textToCopy +
            `\n${intl.formatMessage({
                ...messages.pleaseMakeSurePoints
            })}\n\n${
                locale === 'de'
                    ? intl.formatMessage({
                          ...messages.includingOptionalDocs
                      })
                    : ''
            }`;

        navigator.clipboard.writeText(textToCopy).catch(() => {
            console.error('Could not copy to clipboard');
        });
    }

    function generateMailToLink() {
        if (requiredDocs && optionalDocs) {
            const mailtoBody = `${generateBodyTextString({
                docRequirements: requiredDocs,
                newLineBreak: '%0D%0A',
                intl: intl
            })}${
                locale === 'de'
                    ? generateBodyTextString({
                          docRequirements: optionalDocs,
                          newLineBreak: '%0D%0A',
                          intl: intl
                      })
                    : ''
            }`;
            return `mailto:?body=Hello %0D%0A %0D%0A I am applying for a loan with iwoca and to verify my trading history I am required to provide several documents. Please can you send me the documents mentioned in the requirement list beneath. %0D%0A %0D%0A ${mailtoBody} %0D%0A ${intl.formatMessage(
                {
                    ...messages.pleaseMakeSurePoints
                }
            )} %0D%0A %0D%0A ${
                locale === 'de'
                    ? intl.formatMessage({
                          ...messages.includingOptionalDocs
                      })
                    : ''
            } &subject=Help with documents for new loan`;
        }
    }

    return (
        <div className={classNames('form-wrapper', styles.wrapper)}>
            <h3
                className={styles.title}
                onClick={() => {
                    if (isContentOpen === false) {
                        tracking.ampli.shareRequiredDocumentsOpened({
                            ...getAmplitudeDefaultEventProperties()
                        });
                    }

                    if (isContentOpen === true) {
                        tracking.ampli.shareRequiredDocumentsClosed({
                            ...getAmplitudeDefaultEventProperties()
                        });
                    }

                    setIsContentOpen(!isContentOpen);
                }}>
                <FormattedMessage {...messages.title} />
                <span className={styles.titleIcon}>
                    <Icon icon={isContentOpen ? 'angleUp' : 'angleDown'} />
                </span>
            </h3>
            {isContentOpen && (
                <>
                    <p className={styles.subTitle}>
                        <FormattedMessage {...messages.subTitle} />
                    </p>
                    <Button
                        variant="tertiary"
                        size="medium"
                        onClick={(
                            event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                            event.preventDefault();

                            tracking.ampli.emailRequiredDocumentsClicked({
                                ...getAmplitudeDefaultEventProperties()
                            });

                            window.location.href = generateMailToLink()!;
                        }}
                        target="_blank">
                        <FormattedMessage {...messages.shareRequirementList} />
                        <Icon icon="emailOutline" />
                    </Button>

                    <div
                        className={styles.requirementsList}
                        ref={requirementListRef}
                        data-testid="shareRequirements_requirementsList">
                        <h4>
                            <FormattedMessage
                                {...messages.shareRequiremenTitle}
                            />
                        </h4>
                        {renderedDocumentRequirements()}
                        <p>
                            <strong>
                                <FormattedMessage
                                    {...messages.pleaseMakeSure}
                                />
                            </strong>
                            <ul className={styles.remember}>
                                {/* DE hack while we are on react-intl v2 */}
                                {intl
                                    .formatMessage(
                                        messages.pleaseMakeSurePoints
                                    )
                                    .split('\r\n')
                                    .map((string) => (
                                        <li key={string}>{string}</li>
                                    ))}
                            </ul>
                        </p>
                        {locale === 'de' && (
                            <p>
                                <FormattedMessage
                                    {...messages.includingOptionalDocs}
                                />
                            </p>
                        )}
                    </div>
                    <div className={styles.buttonContainer}>
                        <Button
                            variant="tertiary"
                            size="medium"
                            className={styles.copyButton}
                            onClick={(
                                event: React.MouseEvent<HTMLButtonElement>
                            ) => {
                                event.preventDefault();

                                tracking.ampli.copyRequiredDocumentsClicked({
                                    ...getAmplitudeDefaultEventProperties()
                                });

                                copyToClipBoard();
                                setIsCopyClicked(true);
                                setTimeout(() => setIsCopyClicked(false), 3000);
                            }}>
                            <FormattedMessage {...messages.copyRequirements} />{' '}
                            <Icon icon="copyOutline" />
                        </Button>
                        {isCopyClicked && (
                            <>
                                <Icon
                                    icon="checkCircleFilled"
                                    className={styles.copyCheckIcon}
                                />
                                <p className={styles.copyText}>
                                    <FormattedMessage
                                        {...messages.listCopied}
                                    />
                                </p>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default injectIntl(BusinessAccountsShareRequirements);
